import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// ─── Palette tokens ───────────────────────────────────────────────────────────
const RED    = "#F72E41";
const RED_DK = "#c41730";
const NAVY   = "#070724";
const WHITE  = "#ffffff";
const FONT   = "Como, sans-serif";

// ─── Animated confetti dots (CSS-only) ───────────────────────────────────────
const ConfettiDot = ({ color, delay, x, size }) => (
  <div
    style={{
      position: "absolute",
      width: size,
      height: size,
      borderRadius: "50%",
      background: color,
      left: `${x}%`,
      top: "-10px",
      animation: `fall 2.4s ${delay}s ease-in forwards`,
      opacity: 0.9,
    }}
  />
);

const confettiItems = [
  { color: RED,     delay: 0,    x: 10,  size: 10 },
  { color: "#F59E0B", delay: 0.15, x: 25, size: 7  },
  { color: NAVY,    delay: 0.3,  x: 40,  size: 9  },
  { color: RED_DK,  delay: 0.1,  x: 55,  size: 6  },
  { color: "#F59E0B", delay: 0.4, x: 68, size: 11 },
  { color: RED,     delay: 0.25, x: 80,  size: 8  },
  { color: NAVY,    delay: 0.5,  x: 92,  size: 7  },
];

// ─── FeatureChip ─────────────────────────────────────────────────────────────
const FeatureChip = ({ icon, text }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "rgba(247,46,65,0.07)",
      border: "1px solid rgba(247,46,65,0.18)",
      borderRadius: "999px",
      padding: "6px 16px",
      fontSize: "13px",
      color: "#1E1E1E",
      fontWeight: 600,
      fontFamily: FONT,
    }}
  >
    <span>{icon}</span>
    <span>{text}</span>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// PaymentSuccess
// ═══════════════════════════════════════════════════════════════════════════════
export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  return (
    <>
      <style>{`
        @keyframes fall {
          0%   { transform: translateY(0) rotate(0deg);   opacity: 0.9; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes pop-in {
          0%   { transform: scale(0.5); opacity: 0; }
          70%  { transform: scale(1.1); }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes slide-up {
          0%   { transform: translateY(24px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 1; }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(1);   opacity: 0.4; }
          50%  { transform: scale(1.18); opacity: 0; }
          100% { transform: scale(1);   opacity: 0; }
        }
      `}</style>

      {/* Full-page container */}
      <div
        style={{
          minHeight: "100vh",
          background: WHITE,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 24px",
          position: "relative",
          overflow: "hidden",
          fontFamily: FONT,
        }}
      >
        {/* Confetti */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {confettiItems.map((c, i) => (
            <ConfettiDot key={i} {...c} />
          ))}
        </div>

        {/* Subtle bg decoration */}
        <div
          style={{
            position: "absolute",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(247,46,65,0.06) 0%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            pointerEvents: "none",
          }}
        />

        {/* Card */}
        <div
          style={{
            background: WHITE,
            border: "2px solid #f0f0f0",
            borderRadius: "28px",
            padding: "52px 44px 44px",
            maxWidth: "520px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(0,0,0,0.09)",
            position: "relative",
            animation: "slide-up 0.6s ease both",
          }}
        >
          {/* Success icon with pulse */}
          <div
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "28px",
            }}
          >
            {/* Pulse rings */}
            {[1, 2].map((n) => (
              <div
                key={n}
                style={{
                  position: "absolute",
                  width: "96px",
                  height: "96px",
                  borderRadius: "50%",
                  border: `2px solid ${RED}`,
                  animation: `pulse-ring 2s ${n * 0.4}s ease-out infinite`,
                }}
              />
            ))}
            <div
              style={{
                width: "88px",
                height: "88px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "pop-in 0.5s 0.2s cubic-bezier(.34,1.56,.64,1) both",
                boxShadow: `0 8px 28px rgba(247,46,65,0.4)`,
              }}
            >
              <span style={{ fontSize: "40px", lineHeight: 1 }}>✓</span>
            </div>
          </div>

          {/* Top strip */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "5px",
              background: `linear-gradient(90deg, ${RED}, #ff6b7a)`,
              borderRadius: "28px 28px 0 0",
            }}
          />

          <h1
            style={{
              fontSize: "28px",
              fontWeight: 900,
              color: "#0f172a",
              margin: "0 0 10px",
              letterSpacing: "-0.5px",
            }}
          >
            ¡Pago exitoso! 🎉
          </h1>

          <p
            style={{
              color: "#64748b",
              fontSize: "15px",
              lineHeight: 1.65,
              margin: "0 0 32px",
            }}
          >
            Tu suscripción se ha activado correctamente. Ya tienes acceso a todas las herramientas de tu nuevo plan.
          </p>

          {/* Feature access chips */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "36px",
            }}
          >
            <FeatureChip icon="📊" text="Análisis avanzados" />
            <FeatureChip icon="🚀" text="Pedidos a domicilio" />
            <FeatureChip icon="🔔" text="Llamados a mesero" />
            <FeatureChip icon="✨" text="Mejora de imágenes IA" />
            <FeatureChip icon="🌐" text="Menú público online" />
          </div>

          {/* Divider */}
          <div style={{ height: "1px", background: "#e2e8f0", marginBottom: "28px" }} />

          {/* Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <button
              onClick={() => navigate("/")}
              style={{
                background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
                color: WHITE,
                border: "none",
                borderRadius: "12px",
                padding: "14px 24px",
                fontSize: "15px",
                fontWeight: 800,
                fontFamily: FONT,
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(247,46,65,0.35)",
                transition: "all 0.2s ease",
                letterSpacing: "0.03em",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
            >
              🏠 Volver al Dashboard
            </button>
            <button
              onClick={() => navigate("/")}
              style={{
                background: "transparent",
                color: RED,
                border: `2px solid rgba(247,46,65,0.3)`,
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: 700,
                fontFamily: FONT,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(247,46,65,0.06)";
                e.currentTarget.style.borderColor = RED;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(247,46,65,0.3)";
              }}
            >
              ⚙️ Empezar a configurar mi restaurante
            </button>
          </div>
        </div>

        {/* Footer copy */}
        <p style={{ color: "#94a3b8", fontSize: "12px", marginTop: "24px" }}>
          Recibirás un correo de confirmación con los detalles de tu suscripción.
        </p>
        {sessionId && (
          <p style={{ color: "#cbd5e1", fontSize: "11px", marginTop: "4px", fontFamily: "monospace" }}>
            Ref: {sessionId}
          </p>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PaymentCancel
// ═══════════════════════════════════════════════════════════════════════════════
export function PaymentCancel() {
  const navigate = useNavigate();

  return (
    <>
      <style>{`
        @keyframes slide-up {
          0%   { transform: translateY(24px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 1; }
        }
        @keyframes wobble {
          0%,100% { transform: rotate(0deg); }
          20%     { transform: rotate(-8deg); }
          40%     { transform: rotate(8deg); }
          60%     { transform: rotate(-5deg); }
          80%     { transform: rotate(5deg); }
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background: WHITE,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 24px",
          fontFamily: FONT,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle background decoration */}
        <div
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(120,120,120,0.05) 0%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            background: WHITE,
            border: "2px solid #f0f0f0",
            borderRadius: "28px",
            padding: "52px 44px 44px",
            maxWidth: "520px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
            position: "relative",
            animation: "slide-up 0.6s ease both",
          }}
        >
          {/* Top strip — neutral gray */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "5px",
              background: "linear-gradient(90deg, #94a3b8, #cbd5e1)",
              borderRadius: "28px 28px 0 0",
            }}
          />

          {/* Icon */}
          <div
            style={{
              width: "88px",
              height: "88px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #e2e8f0, #cbd5e1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 28px",
              animation: "wobble 0.8s 0.3s ease both",
              boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
            }}
          >
            <span style={{ fontSize: "40px", lineHeight: 1 }}>😕</span>
          </div>

          <h1
            style={{
              fontSize: "26px",
              fontWeight: 900,
              color: "#0f172a",
              margin: "0 0 10px",
              letterSpacing: "-0.5px",
            }}
          >
            Pago no completado
          </h1>

          <p
            style={{
              color: "#64748b",
              fontSize: "15px",
              lineHeight: 1.65,
              margin: "0 0 12px",
            }}
          >
            El proceso de pago no se pudo completar o fue cancelado.
          </p>
          <p
            style={{
              color: "#94a3b8",
              fontSize: "13px",
              lineHeight: 1.6,
              margin: "0 0 32px",
            }}
          >
            No se realizó ningún cargo a tu cuenta. Puedes intentarlo nuevamente cuando quieras.
          </p>

          {/* Info chips */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "32px",
            }}
          >
            {[
              { icon: "🔒", text: "Sin cargos realizados" },
              { icon: "🔄", text: "Puedes intentarlo de nuevo" },
              { icon: "💬", text: "Soporte disponible" },
            ].map(({ icon, text }) => (
              <span
                key={text}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "999px",
                  padding: "5px 14px",
                  fontSize: "12px",
                  color: "#475569",
                  fontWeight: 600,
                }}
              >
                {icon} {text}
              </span>
            ))}
          </div>

          <div style={{ height: "1px", background: "#e2e8f0", marginBottom: "28px" }} />

          {/* Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <button
              onClick={() => navigate("/")}
              style={{
                background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
                color: WHITE,
                border: "none",
                borderRadius: "12px",
                padding: "14px 24px",
                fontSize: "15px",
                fontWeight: 800,
                fontFamily: FONT,
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(247,46,65,0.35)",
                transition: "all 0.2s ease",
                letterSpacing: "0.03em",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
            >
              🔄 Intentar de nuevo
            </button>

            <button
              onClick={() => navigate("/")}
              style={{
                background: "transparent",
                color: "#64748b",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: 700,
                fontFamily: FONT,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              🏠 Volver al Dashboard
            </button>

            <a
              href="mailto:soporte@eatery.com"
              style={{
                color: "#94a3b8",
                fontSize: "13px",
                fontWeight: 600,
                textDecoration: "none",
                display: "block",
                marginTop: "4px",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = RED)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
            >
              💬 Contactar soporte
            </a>
          </div>
        </div>

        <p style={{ color: "#cbd5e1", fontSize: "12px", marginTop: "24px" }}>
          Si crees que fue un error, revisa tu correo o contáctanos.
        </p>
      </div>
    </>
  );
}

